import React, { useEffect, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Icons as GridIcons,
  Column,
  GeneralActions,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { Color, Colors } from "../../../../../../components/Form/Button";
import Breadcrumb from "../../../../../../components/Generic/Breadcrump";
import ConfirmChangeStatus from "../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Location } from "../../../../../../models/location";
import { NotifyParty } from "../../../../../../models/notify.party";
import { Row } from "../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import NotifyPartyManageModal from "./components/ManageModal";
import { NotifyPartyPageProvider } from "./context/NotifyParty";
import { useActions } from "./hooks/useAction";
import { BadgeActive, BadgeInactive, ChangeAllStatusButton, FiEyeButton, Wrapper } from "./styles";

const hideSearch = true;

const NotifyPartysPageComponent: React.FC = () => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const {
    variables,
    setNotifyParty,
    createNotifyParty,
    updateNotifyParty,
    setHasErrors,
    setCurrentPage,
    setPageSize,
    handleSearch,
    handleFilter,
    handleSort,
  } = useActions();

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [isOnViewMode, setIsOnViewMode] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => {
    if (status === Status.ACTIVE) {
      return <BadgeActive>{status}</BadgeActive>;
    }

    return <BadgeInactive>{status}</BadgeInactive>;
  };

  const editNotifyParty = (index: number) => {
    if (variables.hasErrors !== undefined) setHasErrors(undefined);
    let notifyPartyList: NotifyParty[] = [];

    try {
      notifyPartyList = JSON.parse(JSON.stringify(variables.notifyPartyList));
    } catch {
      notifyPartyList = [...variables.notifyPartyList];
    }
    const find = notifyPartyList.find((branch) => Number(branch.id) === Number(index));

    if (find) {
      setNotifyParty(find);
      setIsOnViewMode(false);
      setModalOpened(true);
    }
  };

  const viewNotifyParty = (index: number) => (
    <Tooltip title="View" placement="top">
      <div>
        <FiEyeButton
          size={18}
          onClick={() => {
            if (variables.hasErrors !== undefined) setHasErrors(undefined);
            const find = variables.notifyPartyList.find(
              (notifyParty) => Number(notifyParty.id) === Number(index)
            );

            if (find) {
              setNotifyParty(find);
              setIsOnViewMode(true);
              setModalOpened(true);
            }
          }}
        />
      </div>
    </Tooltip>
  );

  const changeNotifyPartyStatus = (index: number) => {
    const find = variables.notifyPartyList.find(
      (notifyParty) => Number(notifyParty.id) === Number(index)
    );

    if (find) {
      updateNotifyParty({
        id: Number(index),
        notifyParty: {
          ...find,
          status: find.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderChangeAllStatus = () => {
    if (rows.length > 0) {
      return (
        <ChangeAllStatusButton
          tooltip={translate("changeStatus")}
          data-cy="inactiveStatus"
          onClick={() => setOpenConfirmation(true)}
          customColor={Color.INFO}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={17} className="mr-2" />
          {translate("changeStatus")}
        </ChangeAllStatusButton>
      );
    }

    return null;
  };

  const handleSaveNotifyPartySelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, variables.notifyPartyList);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "beCode", title: translate("beCode"), searchIdentifier: "be_code" },
    { name: "name", title: translate("name") },
    {
      name: "location",
      title: translate("location"),
      formatText: (location: Location) => location?.longAddress,
      searchIdentifier: "location.long_address",
      hideSearch: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
      hideSearch,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleActive", "handleNewRecord"],
    },
    handleNewRecord: {
      text: translate("create"),
      action: () => {
        if (variables.hasErrors !== undefined) setHasErrors(undefined);
        setIsOnViewMode(false);
        setNotifyParty(undefined);
        setModalOpened(true);
      },
      tooltip: translate("create"),
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("beCode"),
          column: "beCode",
        },
        {
          name: translate("name"),
          column: "name",
        },
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
      ],
      onChange: handleFilter,
    },
    handleActive: renderChangeAllStatus(),
  };

  useEffect(() => {
    if (variables.hasErrors !== undefined && !variables.hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.success.message.notifyPartyList.id",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [variables.hasErrors]);

  return (
    <Wrapper data-cy="branchDataList">
      {/* <header className="mb-4">
        <Breadcrumb
          items={[
            { title: translate("configurations") },
            {
              title: translate("notifyParty"),
              color: "#465869",
              dataCy: "breadcrumbsSubTitle",
            },
          ]}
        />
      </header> */}

      <ShipeeziNewGrid
        data={variables.notifyPartyList}
        columns={columns}
        totalItems={variables.total}
        loadingData={variables.isFetching || variables.isCreating || variables.isUpdating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={variables.pageSize}
        pageSizeList={[15, 40, 60, 100]}
        currentPage={variables.page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveNotifyPartySelected(rows)}
        rowActions={{
          orderBy: ["handleEdit", "handleView", "handleChange"],
          handleEdit: (notifyPartyId) => editNotifyParty(Number(notifyPartyId)),
          handleChange: (notifyPartyId) => changeNotifyPartyStatus(Number(notifyPartyId)),
        }}
        rowActionsExtra={[{ title: "View", key: "handleView", buttonAndAction: viewNotifyParty }]}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />

      {modalOpened && (
        <NotifyPartyManageModal
          notifyPartyToUpdate={variables.notifyParty}
          isOpened={modalOpened}
          isLoading={variables.isUpdating || variables.isCreating}
          isView={isOnViewMode}
          onCloseModal={() => setModalOpened(false)}
          onSubmit={(notifyParty: NotifyParty) => {
            if (notifyParty.id) {
              updateNotifyParty(
                { id: notifyParty.id, notifyParty },
                {
                  onSuccess: () => {
                    setModalOpened(false);
                  },
                }
              );
            } else
              createNotifyParty(
                {
                  ...notifyParty,
                  companyId: currentUser?.companyId,
                },
                {
                  onSuccess: () => {
                    setModalOpened(false);
                  },
                }
              );
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          closeModal={() => setOpenConfirmation(false)}
          rows={rows}
          isLoading={variables.isUpdating}
          hasErrors={variables.hasErrors}
          update={(rowId, status) => {
            if (rowId) {
              updateNotifyParty({ id: rowId, notifyParty: { status } as NotifyParty });
              setClearSelection(true);
            }
          }}
        />
      )}
    </Wrapper>
  );
};

const NotifyPartysPage = () => {
  return (
    <NotifyPartyPageProvider>
      <NotifyPartysPageComponent />
    </NotifyPartyPageProvider>
  );
};

export default NotifyPartysPage;
